<script setup lang="ts" generic="V extends string | number = string | number">
// Merges legacy OSelect + OCustomSelect (215 call-sites) into one searchable, teleported,
// keyboard-accessible Select on Headless Combobox + the useFloating adapter. The many bespoke
// legacy flags (tableSelect/showLogo/channelField/…) are intentionally NOT ported — features
// express those via slots/meta or thin wrappers (ledger 02-B open item).
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'

import type { SelectOption } from './types'

import { injectUiLabels } from '@/shared/i18n'
import { useComboboxClickOpen, useViewportRestore } from '@/shared/ui/composables'
import { useFloating } from '@/shared/ui/floating'
import { Icon } from '@/shared/ui/icon'
import { OptionVisual } from '@/shared/ui/option-visual'
import { FloatingLayer, OverlayPortal } from '@/shared/ui/overlay'
import type { UiSize, UiAsyncState, UiPlacement } from '@/shared/ui/types'

// Tailwind's spacing/text scale doesn't hit these exact steps (legacy v3 sizing recipe), so the
// field height/font-size per `size` is resolved here instead of via a CSS descendant selector.
const FIELD_SIZE_CLASS: Record<UiSize, string> = {
  xs: 'h-7 text-xs',
  sm: 'h-8 text-xs',
  md: 'h-10 text-[0.8125rem]',
  lg: 'h-12 text-sm',
  xl: 'h-14 text-sm',
}

// The `picker` trigger's own tone. `link` is the inline "N related metrics" affordance: a text
// link that opens the picker, right-aligned because it always sits at the end of its row. It is a
// primitive-owned variant rather than a host override because the trigger is the primitive's own
// element — a feature styling it from outside would be a reach-in (contract 02 §16).
const TRIGGER_TONE_CLASS: Record<'default' | 'link', string> = {
  default: 'text-primary',
  link: 'justify-end gap-1.5 text-info text-xs font-bold capitalize hover:underline',
}

const model = defineModel<V | null>({ default: null })

const props = withDefaults(
  defineProps<{
    options: SelectOption<V>[]
    label?: string
    hideLabel?: boolean
    placeholder?: string
    size?: UiSize
    searchable?: boolean
    clearable?: boolean
    clearLabel?: string
    searchPlaceholder?: string
    emptyLabel?: string
    disabled?: boolean
    state?: UiAsyncState
    variant?: 'default' | 'picker' | 'filled'
    triggerTone?: 'default' | 'link'
    placement?: UiPlacement
    offset?: number
    panelWidth?: string
  }>(),
  {
    size: 'md',
    hideLabel: false,
    searchable: false,
    clearable: false,
    disabled: false,
    state: 'ready',
    variant: 'default',
    triggerTone: 'default',
    placement: 'bottom-start',
    offset: 4,
  },
)

const emit = defineEmits<{ change: [value: V | null] }>()

const uiLabels = injectUiLabels()
const resolvedClearLabel = computed(() => props.clearLabel ?? uiLabels.clear)
const resolvedSearchPlaceholder = computed(() => props.searchPlaceholder ?? uiLabels.search)
const resolvedEmptyLabel = computed(() => props.emptyLabel ?? uiLabels.noResults)

const query = ref('')
const selectedOption = computed(() => props.options.find((o) => o.value === model.value) ?? null)

// Legacy parity (v3 OrphexCustomSelect's `orderOptionsBySelection`): the current selection floats to
// the top of the list so a picker with a long option set (e.g. report columns) never buries it.
function orderBySelection(list: SelectOption<V>[]): SelectOption<V>[] {
  if (model.value === null) return list
  return [...list].sort((left, right) => {
    const leftSelected = left.value === model.value ? 1 : 0
    const rightSelected = right.value === model.value ? 1 : 0
    return rightSelected - leftSelected
  })
}

const filtered = computed(() => {
  if (!props.searchable || query.value === '') return orderBySelection(props.options)
  const q = query.value.toLowerCase()
  return orderBySelection(props.options.filter((o) => o.label.toLowerCase().includes(q)))
})

const reference = ref<HTMLElement | null>(null)
const floatingEl = ref<HTMLElement | null>(null)
const { floatingStyles } = useFloating(reference, floatingEl, {
  placement: props.placement,
  offset: props.offset,
  // Options are teleported to the document overlay root. A fixed strategy keeps option focus in
  // viewport coordinates; absolute document coordinates can make Headless UI scroll the page when
  // it activates the selected option.
  strategy: 'fixed',
})
// Headless UI closes the teleported options before returning focus. Explicitly restore it with
// scroll suppression so chart/table picker selections cannot jump a long report page to top.
const { preserveViewportAfterFocus } = useViewportRestore()

function onChange(value: V | null): void {
  model.value = value
  emit('change', value)
  preserveViewportAfterFocus(() =>
    reference.value
      ?.querySelector<HTMLElement>('input, button:not([disabled])')
      ?.focus({ preventScroll: true }),
  )
}

const openViaChevron = useComboboxClickOpen(
  reference,
  '.ui-select__chevron',
  () => props.searchable && props.variant === 'default',
)

function onControlClick(event: MouseEvent): void {
  preserveViewportAfterFocus()
  openViaChevron(event)
}

function clear(): void {
  onChange(null)
}

function handleEscape(event: KeyboardEvent): void {
  if (
    event.key === 'Escape' &&
    document.activeElement instanceof HTMLElement &&
    floatingEl.value?.contains(document.activeElement)
  ) {
    window.setTimeout(
      () => reference.value?.querySelector('button')?.focus({ preventScroll: true }),
      0,
    )
  }
}

function handlePickerTab(event: KeyboardEvent): void {
  if (props.variant !== 'picker') return
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()
  const target = event.currentTarget
  if (!(target instanceof HTMLElement)) return
  const trigger = reference.value?.querySelector<HTMLButtonElement>('button')
  // Land focus back inside the parent focus scope before Combobox unmounts its portal. Otherwise
  // browsers briefly report `body` as active and the parent Popover correctly treats that as exit.
  trigger?.focus({ preventScroll: true })
  target.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))

  window.setTimeout(() => {
    if (!trigger) return
    if (event.shiftKey) {
      trigger.focus({ preventScroll: true })
      return
    }

    const dialog = reference.value?.closest('[role="dialog"]')
    const focusable = [
      ...(dialog?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      ) ?? []),
    ].filter((element) => !floatingEl.value?.contains(element))
    const triggerIndex = focusable.indexOf(trigger)
    focusable[triggerIndex + 1]?.focus({ preventScroll: true })
  }, 0)
}

onMounted(() => window.addEventListener('keydown', handleEscape, true))
onUnmounted(() => window.removeEventListener('keydown', handleEscape, true))
</script>

<template>
  <Combobox
    v-slot="{ open }"
    :model-value="model"
    :disabled="disabled"
    as="div"
    class="ui-select relative w-full font-sans"
    @update:model-value="(v: V | null) => onChange(v)"
  >
    <label
      v-if="label"
      class="ui-select__label absolute -top-2 left-2 z-10 rounded-sm bg-[linear-gradient(to_bottom,transparent_40%,var(--ui-color-surface)_50%)] px-1 text-xs font-medium text-muted"
      :class="{ 'ui-select__label--sr-only sr-only': hideLabel }"
    >
      {{ label }}
    </label>

    <div
      ref="reference"
      class="ui-select__control relative flex items-center"
      :class="[
        variant === 'picker'
          ? 'border-0 bg-transparent shadow-none focus-within:border-transparent focus-within:shadow-none'
          : 'rounded-sm border border-border bg-surface shadow-input focus-within:border-[color:var(--ui-color-ring)] focus-within:shadow-[0_0_0_1px_var(--ui-color-ring)]',
        { 'is-disabled cursor-not-allowed opacity-60': disabled },
      ]"
      @click.capture="onControlClick"
    >
      <OptionVisual
        v-if="variant === 'default' && searchable && selectedOption"
        class="ui-select__selected-visual absolute left-3 z-raised"
        :visual="selectedOption.meta?.visual"
        :label="selectedOption.label"
        :size="16"
      />
      <ComboboxButton
        v-if="variant === 'picker'"
        class="ui-select__picker-trigger inline-flex w-full min-w-0 items-center border-0 bg-transparent outline-none focus-visible:rounded-sm focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)]"
        :class="TRIGGER_TONE_CLASS[triggerTone]"
      >
        <slot name="trigger" :open="open" :selected-option="selectedOption">
          <span class="ui-select__picker-value inline-flex min-w-0 items-center gap-2xs">
            <OptionVisual
              v-if="selectedOption"
              :visual="selectedOption.meta?.visual"
              :label="selectedOption.label"
              :size="16"
            />
            <span class="min-w-0 truncate">{{ selectedOption?.label ?? placeholder }}</span>
          </span>
        </slot>
      </ComboboxButton>
      <ComboboxInput
        v-else-if="searchable"
        class="ui-select__field w-full min-w-0 flex-1 cursor-pointer border-0 bg-transparent pl-3 pr-8 text-left text-fg outline-none"
        :class="[FIELD_SIZE_CLASS[size], { 'pl-9': selectedOption?.meta?.visual }]"
        :display-value="() => selectedOption?.label ?? ''"
        :placeholder="placeholder"
        @change="query = ($event.target as HTMLInputElement).value"
      />
      <ComboboxButton
        v-else
        class="ui-select__field ui-select__field--button w-full min-w-0 flex-1 cursor-pointer border-0 bg-transparent pl-3 pr-8 text-left text-fg outline-none"
        :class="FIELD_SIZE_CLASS[size]"
      >
        <span v-if="selectedOption" class="ui-select__text flex min-w-0 items-center gap-2xs">
          <OptionVisual
            :visual="selectedOption.meta?.visual"
            :label="selectedOption.label"
            :size="16"
          />
          <span class="min-w-0 truncate">{{ selectedOption.label }}</span>
        </span>
        <span
          v-else
          class="ui-select__text ui-select__text--placeholder block truncate text-muted"
          >{{ placeholder }}</span
        >
      </ComboboxButton>

      <button
        v-if="variant === 'default' && clearable && selectedOption && !disabled"
        type="button"
        class="ui-select__clear absolute right-[1.6rem] inline-flex items-center justify-center border-0 bg-transparent text-xs text-muted hover:text-fg"
        :aria-label="resolvedClearLabel"
        @click.stop="clear"
      >
        <Icon name="x" :size="12" class="ui-select__clear-icon" />
      </button>
      <ComboboxButton
        v-if="variant === 'default'"
        class="ui-select__chevron absolute right-2 inline-flex items-center justify-center border-0 bg-transparent text-xs text-muted pointer-events-none"
        aria-hidden="true"
      >
        <Icon :name="open ? 'chevron-up' : 'chevron-down'" :size="14" />
      </ComboboxButton>
    </div>

    <OverlayPortal>
      <FloatingLayer
        v-if="open"
        v-model:element="floatingEl"
        kind="dropdown"
        class="ui-select__floating"
        :class="
          variant === 'picker'
            ? 'w-max max-w-[min(22rem,var(--ui-floating-available-width,calc(100vw_-_0.75rem)))]'
            : 'w-[min(var(--ui-floating-reference-width,12rem),var(--ui-floating-available-width,calc(100vw_-_0.75rem)))]'
        "
        :style="floatingStyles"
      >
        <div
          class="ui-select__panel"
          :class="[
            variant === 'picker'
              ? 'overflow-hidden rounded-lg border border-border bg-surface shadow-card'
              : 'min-w-48',
          ]"
          :style="{ width: panelWidth }"
        >
          <div
            v-if="variant === 'picker' && searchable"
            class="ui-select__picker-search relative m-1 flex items-center h-9 rounded-md border border-border bg-surface text-muted focus-within:border-[color:var(--ui-color-ring)] focus-within:shadow-[0_0_0_1px_var(--ui-color-ring)]"
          >
            <ComboboxInput
              class="ui-select__picker-search-field h-full w-full bg-transparent px-3 text-fg outline-none [font:inherit]"
              :display-value="() => ''"
              :placeholder="resolvedSearchPlaceholder"
              @change="query = ($event.target as HTMLInputElement).value"
              @keydown.tab.capture="handlePickerTab"
            />
          </div>
          <ComboboxOptions
            class="ui-select__options m-0 list-none overflow-y-auto p-1 text-fg"
            :class="
              variant === 'picker'
                ? 'max-h-[min(15rem,var(--ui-floating-available-height))] rounded-none border-0 py-xs shadow-none'
                : 'max-h-64 w-full min-w-0 rounded-lg border border-border bg-surface shadow-pop'
            "
          >
            <div
              v-if="state === 'loading'"
              class="ui-select__msg px-3 py-2 text-[0.8125rem] text-muted"
            >
              <slot name="loading">…</slot>
            </div>
            <div
              v-else-if="state === 'error'"
              class="ui-select__msg ui-select__msg--error px-3 py-2 text-[0.8125rem] text-danger"
            >
              <slot name="error">!</slot>
            </div>
            <template v-else>
              <div
                v-if="filtered.length === 0"
                class="ui-select__msg px-3 py-2 text-[0.8125rem] text-muted"
              >
                <slot name="empty">{{ resolvedEmptyLabel }}</slot>
              </div>
              <ComboboxOption
                v-for="opt in filtered"
                :key="String(opt.value)"
                v-slot="{ active, selected }"
                :value="opt.value"
                :disabled="opt.disabled ?? false"
                as="template"
              >
                <li
                  class="ui-select__option flex min-h-10 cursor-pointer items-center justify-between gap-3 px-3 py-2 text-xs font-normal leading-5"
                  :class="[
                    variant === 'picker' ? 'rounded-none' : 'rounded-sm',
                    { 'is-active bg-surface-sunken': active },
                    { 'is-selected text-primary font-semibold': selected },
                    { 'is-disabled cursor-not-allowed opacity-50': opt.disabled },
                  ]"
                >
                  <slot name="option" :option="opt" :active="active" :selected="selected">
                    <span class="ui-select__option-copy flex min-w-0 items-center gap-2">
                      <OptionVisual :visual="opt.meta?.visual" :label="opt.label" :size="16" />
                      <span class="ui-select__option-label min-w-0 break-words">
                        {{ opt.label }}
                      </span>
                    </span>
                    <Icon v-if="selected" name="check" :size="14" class="ui-select__check" />
                  </slot>
                </li>
              </ComboboxOption>
            </template>
          </ComboboxOptions>
        </div>
      </FloatingLayer>
    </OverlayPortal>
  </Combobox>
</template>
